import {
 createTheme,
 Shadows,
 Theme,
 ThemeOptions,
} from '@mui/material/styles';
// The import can be in any file that is included in your `tsconfig.json`
import type {} from '@mui/material/themeCssVarsAugmentation';
import type {} from '@mui/x-data-grid-premium/themeAugmentation'; // important

import customColors from './customColors';
import customColorsDark from './customColorsDark';

declare module '@mui/material/styles' {
 interface PaletteOptions {
  kemek: {
   black: string;
   darkestGray: string;
   darkGray: string;
   gray: string;
   lightGray: string;
   lightestGray: string;
   white: string;
   blue: string;
   lightBlue: string;
   green: string;
   lightGreen: string;
   red: string;
   lightRed: string;
   orange: string;
   lightOrange: string;
   yellow: string;
   lightYellow: string;
   bg1: string;
   bg2: string;
  };
 }
 interface Palette {
  kemek: {
   black: string;
   darkestGray: string;
   darkGray: string;
   gray: string;
   lightGray: string;
   lightestGray: string;
   white: string;
   blue: string;
   lightBlue: string;
   green: string;
   lightGreen: string;
   red: string;
   lightRed: string;
   orange: string;
   lightOrange: string;
   yellow: string;
   lightYellow: string;
   bg1: string;
   bg2: string;
  };
 }
}

// create a temporary theme to get the default options
const defaultTheme = createTheme();

const defaultShadows: ThemeOptions['shadows'] = [...defaultTheme.shadows];

const themeOptions = () => {
 let th = createTheme({
  shadows: defaultShadows.map(
   () => '0px 4px 12px 0px rgba(0, 0, 0, 0.08)',
  ) as Shadows,
  colorSchemes: {
   light: {
    palette: {
     kemek: customColors,
     primary: {
      main: customColors.blue,
     },
     secondary: {
      main: customColors.darkGray,
     },
     background: {
      default: customColors.bg2,
      paper: '#ffffff',
     },
     text: {
      primary: customColors.darkestGray,
      secondary: customColors.darkestGray,
      disabled: customColors.gray,
     },
     error: {
      main: customColors.red,
      contrastText: '#ffffff',
     },
     warning: {
      main: '#f3ca36',
      contrastText: '#ffffff',
     },
     info: {
      main: customColors.blue,
      contrastText: '#ffffff',
     },
     success: {
      main: customColors.green,
      contrastText: '#ffffff',
     },

     // amber: {
     //  lev0: amber[50],
     //  lev1: amber[100],
     //  lev2: amber[200],
     //  lev3: amber[300],
     //  lev4: amber[400],
     //  lev5: amber[500],
     //  lev6: amber[600],
     //  lev7: amber[700],
     //  lev8: amber[800],
     //  lev9: amber[900],
     // },
    },
   },
   dark: {
    palette: {
     kemek: customColorsDark,
     primary: {
      main: customColorsDark.blue,
     },
     secondary: {
      main: customColorsDark.darkGray,
     },
     text: {
      primary: customColorsDark.darkestGray,
      secondary: customColorsDark.darkestGray,
      disabled: customColorsDark.gray,
     },
     background: {
      default: customColorsDark.bg2,
      paper: customColorsDark.white,
     },
    },
   },
  },
  defaultColorScheme: 'light',
  typography: {
   fontFamily: 'Poppins',
   fontSize: 12,
   button: {
    textTransform: 'none',
   },
  },
  shape: {
   borderRadius: 4,
  },
 });
 th = createTheme(th, {
  cssVariables: true,

  components: {
   MuiDataGrid: {
    styleOverrides: {
     root: {
      border: (theme: Theme) =>
       theme.palette.mode === 'dark' ? 'none' : '#e0e0e0', // this is not in design
     },
     menu: {
      '& .MuiDataGrid-menuList': {
       padding: 0,
       border: `1px solid ${customColors.lightestGray}`,
       borderRadius: 4,
       '& .MuiDivider-root': {
        display: 'none !important',
       },
      },
     },
    },
   },
   MuiCssBaseline: {
    styleOverrides: {
     body: {
      '& ::-webkit-scrollbar': {
       width: '6px',
       height: '6px',
      },
      '& ::-webkit-scrollbar-track': {
       background: th.palette.kemek.lightestGray,
      },
      '& ::-webkit-scrollbar-thumb': {
       background: th.palette.kemek.gray,
       borderRadius: '3px',
       scrollbarColor: '#969696 #dddddd',
      },
      // column menu
      '& .MuiDataGrid-menuList': {
       padding: 0,
       border: `1px solid ${th.palette.kemek.lightestGray}`,
       borderRadius: 4,
       '& .MuiDivider-root': {
        display: 'none !important',
       },
      },
     },
    },
    MuiIconButton: {
     styleOverrides: {
      root: {
       padding: '4px',
      },
     },
    },
    MuiButton: {
     styleOverrides: {
      root: {
       lineHeight: 1.5,
      },
      outlinedPrimary: {
       borderColor: 'green',
       color: 'green',
       '&:hover': {
        borderColor: '#115293',
        backgroundColor: 'rgba(25, 118, 210, 0.04)',
       },
      },
      outlinedSecondary: {
       borderColor: '#dc004e',
       color: '#dc004e',
       '&:hover': {
        borderColor: '#9a0036',
       },
      },
      sizeMedium: {
       padding: '11px 22px',
      },
      sizeLarge: {},
      endIcon: {
       marginLeft: '12px',
      },
      startIcon: {
       marginRight: '12px',
      },
     },
    },
    MuiAppBar: {
     styleOverrides: {
      colorPrimary: {
       color: th.palette.kemek.darkestGray,
      },
     },
    },
    MuiDialogContent: {
     styleOverrides: {
      root: {
       padding: '16px 24px 0 24px',
      },
     },
    },
    MuiDialogActions: {
     styleOverrides: {
      root: {
       padding: '24px',
       '&>:not(style)~not(style)': {
        background: '5px solid red',
       },
      },
     },
    },
    MuiDivider: {
     styleOverrides: {
      root: {
       borderColor: th.palette.kemek.lightestGray,
      },
     },
    },
    MuiMenu: {
     styleOverrides: {
      paper: {
       border: `1px solid ${th.palette.kemek.lightestGray}`,
      },
     },
    },
    MuiMenuItem: {
     styleOverrides: {
      root: {
       padding: '10px 20px 10px 12px',
       borderBottom: `1px solid ${th.palette.kemek.lightestGray}`,
       '&:last-child': {
        borderBottom: 'none',
       },
      },
     },
    },
    MuiTabs: {
     styleOverrides: {
      root: {
       overflow: 'visible',
      },
      scroller: {
       overflow: 'visible !important',
      },
      indicator: {
       height: '1px',
       bottom: -1,
      },
     },
    },
    MuiTab: {
     styleOverrides: {
      root: {
       fontWeight: 400,
      },
     },
    },
    MuiSelect: {
     styleOverrides: {
      root: {
       '& .MuiSelect-icon': {
        display: 'none', // Hide default icon
       },
       '&::after': {
        content: '""',
        backgroundImage:
         'url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAiIGhlaWdodD0iNyIgdmlld0JveD0iMCAwIDEwIDciIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxnIGlkPSJJY29ucyI+CjxwYXRoIGlkPSJhcnJvdy1ib3R0b20iIGQ9Ik05IDFMNS4wMDIgNUwxIDEiIHN0cm9rZT0iIzJCMkIyQiIgc3Ryb2tlLXdpZHRoPSIxLjUiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIvPgo8L2c+Cjwvc3ZnPgo=")',
        backgroundSize: '100%',
        width: 10,
        height: 7,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        position: 'absolute',
        right: '14px',
        top: '50%',
        transform: 'translateY(-50%)',
        pointerEvents: 'none',
        display: 'block',
        border: 0,
        left: 'auto',
        bottom: 'auto',
       },
      },
     },
    },
    MuiFilledInput: {
     styleOverrides: {
      root: {
       background: th.palette.kemek.white,
       borderRadius: 4,
       border: `1px solid ${th.palette.kemek.lightGray}`,
       '&.Mui-error': {
        border: `1px solid ${th.palette.kemek.red}`,
       },
       '&:hover': {
        background: th.palette.kemek.white,
       },
       '&.Mui-focused': {
        background: th.palette.kemek.white,
       },
       //  '& .MuiFilledInput-input': {},
      },
     },
    },
    MuiTextField: {
     styleOverrides: {
      root: {
       background: 'transparent',
      },
     },
    },
    MuiListItem: {
     styleOverrides: {
      root: {
       '& .MuiTouchRipple-root': {
        display: 'none',
       },
      },
     },
    },
    // MuiDialogTitle: {
    //  styleOverrides: {
    //   root: {
    //    fontSize: 18,
    //    fontWeight: 500,
    //   },
    //  },
    // },
   },
  },
 });
 return th;
};
export default themeOptions;
